import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import VideoCard from '@/components/feature/VideoCard';
import { getCategory, getVideosByCategory, sortVideos, type SortKey } from '@/utils/video';

const PAGE_SIZE = 6;

export default function CategoryPage() {
  const { type } = useParams<{ type: string }>();
  const category = getCategory(type);
  const [sortKey, setSortKey] = useState<SortKey>('latest');
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);

  const allVideos = useMemo(() => (type ? getVideosByCategory(type) : []), [type]);
  const sorted = useMemo(() => sortVideos(allVideos, sortKey), [allVideos, sortKey]);
  const visibleVideos = sorted.slice(0, visibleCount);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [type, sortKey]);

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 宅男视频短视频精选`;
    } else {
      document.title = '分类未找到 - 宅男视频';
    }
  }, [category]);

  const sortOptions: { key: SortKey; label: string; icon: string }[] = [
    { key: 'latest', label: '最新发布', icon: 'ri-time-line' },
    { key: 'views', label: '最多播放', icon: 'ri-fire-line' },
  ];

  if (!category) {
    return (
      <div className="w-full px-4 md:px-6 py-16 md:py-24">
        <div className="mx-auto max-w-lg text-center">
          <span className="mx-auto flex items-center justify-center w-20 h-20 rounded-full bg-accent-100 text-accent-600">
            <i className="ri-emotion-sad-line text-4xl"></i>
          </span>
          <h1 className="mt-6 text-xl md:text-2xl font-bold text-foreground-950">没有找到该分类</h1>
          <p className="mt-3 text-sm text-foreground-600">你访问的分类可能已调整或不存在，返回首页看看其他精彩内容吧。</p>
          <Link
            to="/"
            className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            返回首页
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="w-full bg-primary-900">
        <div className="w-full px-4 md:px-6 py-8 md:py-12">
          <nav className="flex items-center gap-2 text-xs text-primary-200 mb-4">
            <Link to="/" className="hover:text-accent-400 transition-colors cursor-pointer">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-background-50">{category.name}</span>
          </nav>
          <div className="flex items-start gap-4">
            <span className="hidden sm:flex items-center justify-center w-14 h-14 rounded-xl bg-accent-500 text-primary-950 shrink-0">
              <i className={`${category.icon} text-2xl`}></i>
            </span>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-background-50">{category.name} · 短视频精选</h1>
              <p className="mt-2 text-sm text-primary-100 max-w-2xl leading-relaxed">{category.description}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 md:px-6 py-8 md:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 md:mb-8">
          <p className="text-sm text-foreground-600">
            共 <span className="font-semibold text-foreground-950">{allVideos.length}</span> 个视频
          </p>
          <div className="flex items-center gap-1 p-1 rounded-full bg-background-50 border border-background-200 overflow-x-auto">
            {sortOptions.map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setSortKey(option.key)}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm whitespace-nowrap transition-colors cursor-pointer ${
                  sortKey === option.key
                    ? 'bg-primary-500 text-background-50'
                    : 'text-foreground-600 hover:text-foreground-950'
                }`}
              >
                <i className={option.icon}></i>
                {option.label}
              </button>
            ))}
          </div>
        </div>

        {visibleVideos.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
              {visibleVideos.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
            <div className="mt-8 flex flex-col items-center gap-3">
              {visibleCount < sorted.length ? (
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + PAGE_SIZE)}
                  className="inline-flex items-center gap-2 h-11 px-8 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
                >
                  <i className="ri-add-line"></i>
                  加载更多
                </button>
              ) : (
                <p className="text-sm text-foreground-500">已加载该分类全部 {sorted.length} 个视频</p>
              )}
              <span className="text-xs text-foreground-500">
                已显示 {visibleVideos.length} / {sorted.length}
              </span>
            </div>
          </>
        ) : (
          <div className="mx-auto max-w-lg text-center py-16">
            <span className="mx-auto flex items-center justify-center w-20 h-20 rounded-full bg-secondary-100 text-secondary-600">
              <i className="ri-inbox-line text-4xl"></i>
            </span>
            <h2 className="mt-6 text-lg font-bold text-foreground-950">该分类暂无内容</h2>
            <p className="mt-3 text-sm text-foreground-600">内容正在加紧补充中，先看看其他分类吧。</p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button
                type="button"
                onClick={() => setSortKey('latest')}
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 font-medium text-sm whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
              <Link
                to="/"
                className="inline-flex items-center gap-2 h-11 px-6 rounded-md border border-background-300 text-foreground-800 font-medium text-sm whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}